import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { CharacterCount, Focus, Placeholder } from "@tiptap/extensions";
import {
  Bold,
  Code2,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Redo,
  RemoveFormatting,
  Strikethrough,
  Undo,
  Unlink,
} from "lucide-react";

function ToolbarButton({ active, disabled, onClick, children, title }) {
  return (
    <button
      aria-label={title}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border text-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[#4391d1]/30 disabled:cursor-not-allowed disabled:opacity-40 ${
        active
          ? "border-[#4391d1] bg-blue-50 text-[#1278b5]"
          : "border-gray-200 bg-white hover:border-[#4391d1] hover:text-[#1278b5]"
      }`}
      disabled={disabled}
      onClick={onClick}
      title={title}
      type="button"
    >
      {children}
    </button>
  );
}

function ToolbarGroup({ children }) {
  return (
    <div className="flex items-center gap-1 rounded-xl border border-gray-100 bg-gray-50 p-1">
      {children}
    </div>
  );
}

function normalizeUrl(url) {
  const trimmed = String(url || "").trim();
  if (!trimmed) return "";
  if (/^(https?:|mailto:|tel:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export default function RichTextEditor({
  label = "Full description",
  value = "",
  onChange,
  placeholder = "Write a detailed product description...",
  minHeight = "min-h-[220px]",
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      CharacterCount.configure(),
      Focus.configure({
        className: "rich-text-node-focus",
        mode: "deepest",
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: `rich-text-editor-content ${minHeight}`,
      },
    },
    onUpdate({ editor: nextEditor }) {
      onChange?.(nextEditor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const currentHtml = editor.getHTML();
    const nextHtml = value || "";

    if (nextHtml !== currentHtml) {
      editor.commands.setContent(nextHtml, { emitUpdate: false });
    }
  }, [editor, value]);

  const words = editor?.storage.characterCount.words() || 0;
  const characters = editor?.storage.characterCount.characters() || 0;

  const setLink = () => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href || "";
    const url = window.prompt("Enter link URL", previousUrl);

    if (url === null) return;

    const href = normalizeUrl(url);

    if (!href) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  return (
    <div className="block md:col-span-2">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="block text-xs font-black uppercase text-gray-500">
          {label}
        </span>
        <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-black uppercase text-gray-500">
          {words} words / {characters} characters
        </span>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition focus-within:border-[#4391d1] focus-within:ring-4 focus-within:ring-blue-100">
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 bg-white px-3 py-3">
          <ToolbarGroup>
            <ToolbarButton
              active={editor?.isActive("paragraph")}
              disabled={!editor}
              onClick={() => editor.chain().focus().setParagraph().run()}
              title="Paragraph"
            >
              <Pilcrow size={16} />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive("heading", { level: 1 })}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              title="Heading 1"
            >
              <Heading1 size={16} />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive("heading", { level: 2 })}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              title="Heading 2"
            >
              <Heading2 size={16} />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive("heading", { level: 3 })}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              title="Heading 3"
            >
              <Heading3 size={16} />
            </ToolbarButton>
          </ToolbarGroup>

          <ToolbarGroup>
            <ToolbarButton
              active={editor?.isActive("bold")}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleBold().run()}
              title="Bold"
            >
              <Bold size={16} />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive("italic")}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title="Italic"
            >
              <Italic size={16} />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive("strike")}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleStrike().run()}
              title="Strikethrough"
            >
              <Strikethrough size={16} />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive("code")}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleCode().run()}
              title="Inline code"
            >
              <Code2 size={16} />
            </ToolbarButton>
          </ToolbarGroup>

          <ToolbarGroup>
            <ToolbarButton
              active={editor?.isActive("bulletList")}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              title="Bullet list"
            >
              <List size={16} />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive("orderedList")}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              title="Numbered list"
            >
              <ListOrdered size={16} />
            </ToolbarButton>
            <ToolbarButton
              active={editor?.isActive("blockquote")}
              disabled={!editor}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              title="Quote"
            >
              <Quote size={16} />
            </ToolbarButton>
            <ToolbarButton
              disabled={!editor}
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
              title="Divider"
            >
              <Minus size={16} />
            </ToolbarButton>
          </ToolbarGroup>

          <ToolbarGroup>
            <ToolbarButton
              active={editor?.isActive("link")}
              disabled={!editor}
              onClick={setLink}
              title="Add or edit link"
            >
              <Link2 size={16} />
            </ToolbarButton>
            <ToolbarButton
              disabled={!editor || !editor.isActive("link")}
              onClick={() => editor.chain().focus().extendMarkRange("link").unsetLink().run()}
              title="Remove link"
            >
              <Unlink size={16} />
            </ToolbarButton>
          </ToolbarGroup>

          <ToolbarGroup>
            <ToolbarButton
              disabled={!editor}
              onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
              title="Clear formatting"
            >
              <RemoveFormatting size={16} />
            </ToolbarButton>
            <ToolbarButton
              disabled={!editor || !editor.can().undo()}
              onClick={() => editor.chain().focus().undo().run()}
              title="Undo"
            >
              <Undo size={16} />
            </ToolbarButton>
            <ToolbarButton
              disabled={!editor || !editor.can().redo()}
              onClick={() => editor.chain().focus().redo().run()}
              title="Redo"
            >
              <Redo size={16} />
            </ToolbarButton>
          </ToolbarGroup>
        </div>

        <div className="bg-gray-50/70">
          <EditorContent editor={editor} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 bg-white px-4 py-2 text-xs font-semibold text-gray-400">
          <span>Supports headings, lists, quotes, dividers, code, and pasted formatted text.</span>
          <span>{editor?.isFocused ? "Editing" : "Ready"}</span>
        </div>
      </div>
    </div>
  );
}
